import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { FlightTicketPropType } from 'PropTypes';
import { Card, Button } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { toastr } from 'Helpers/ToastHelper';
import { mapSettingsKeywords } from 'Helpers/SettingsHelper';
import { FlightTicketApiService, SettingsApiService } from 'Services/ApiServices';
import { setLoader } from 'Store/Actions/Loading/LoadingAction';
import { DS_F2W_Business_Travel_Team } from 'Constants/F2wRolesConstants';
import FlightDetailsSection from './FlightCard/FlightDetailsSection';
import TravelTypeSection from './FlightCard/TravelTypeSection';
import FlightCostSection from './FlightCard/FlightCostSection';
import HotelSection from './FlightCard/HotelSection';
import CNXModCostSection from './FlightCard/CNXModCostSection';
import { calculateTotalCost } from './FlightCard/calculateTotalCost';
import { useApiData } from 'Hooks';

const FlightCard = (props) => {
  const {
    flyToWorkTicketInfo,
    setRefreshComponent,
    flight,
    setShowAddFlightModal,
    showCloseButtonOnModal,
    onTotalChange,
    isLockRequired,
    resetLockTimers,
  } = props;
  const dispatch = useDispatch();
  const { data: settings } = useApiData(() => SettingsApiService.getAll());
  const user = useSelector((state) => state.currentUser);
  const userRoles = user?.roles;

  const [currentSelectedFlight, setCurrentSelectedFlight] = useState({});
  const [validationFlightFormErrors, setValidationFlightFormErrors] = useState({});

  const options = useMemo(() => {
    const kw = Array.isArray(settings?.keywords) ? settings.keywords : [];
    return {
      costCentreOptions: mapSettingsKeywords(kw, 'cost_centers'),
      validLuggageSelectOptions: mapSettingsKeywords(kw, 'luggage'),
      currencySelectOptions: mapSettingsKeywords(kw, 'currency'),
      mappedTravelTypes: mapSettingsKeywords(kw, 'travel_types'),
      mappedPaymentMethods: mapSettingsKeywords(kw, 'payment_methods'),
      airports: mapSettingsKeywords(kw, 'airports', (o) => `${o.value} - ${o.label}`),
      whoOptions: mapSettingsKeywords(kw, 'cnxModWho'),
      reasonOptions: mapSettingsKeywords(kw, 'cnxModReason'),
    };
  }, [settings]);

  const totalCost = calculateTotalCost(currentSelectedFlight);

  const numericTotal =
    (Number(currentSelectedFlight.xbagCost || 0) || 0) +
    (Number(currentSelectedFlight.flightCost || 0) || 0) +
    (Number(currentSelectedFlight.otherTransportCost || 0) || 0) +
    (Number(currentSelectedFlight.hotelCost || 0) || 0) +
    // Include sum of all cnxModCosts (positive and negative)
    (currentSelectedFlight.cnxModCosts || []).reduce((sum, entry) => {
      const cost = Number(entry?.cost || 0);
      return sum + (Number.isNaN(cost) ? 0 : cost);
    }, 0);

  useEffect(() => {
    setCurrentSelectedFlight({
      id: flight.id,
      confirmedFlightDate: flight.confirmedFlightDate,
      bookingReference: flight.bookingReference,
      flightNumber: flight.flightNumber,
      via: flight.via,
      departureAirport: flight.departureAirport,
      flightDepartureTime: flight.flightDepartureTime,
      arrivalAirport: flight.arrivalAirport,
      flightArrivalTime: flight.flightArrivalTime,
      travelType: flight.travelType,
      paymentMethod: flight.paymentMethod,
      luggage: flight.luggage || '',
      xbagCost: flight.xbagCost || 0,
      flightCost: flight.flightCost || 0,
      hotelCost: flight.hotelCost || 0,
      costCentre: flight.costCentre || '',
      otherTransportCost: flight.otherTransportCost || 0,
      currency: flight.currency,
      hotelName: flight.hotelName,
      hotelCheckInDate: flight.hotelCheckInDate,
      hotelCheckOutDate: flight.hotelCheckOutDate,
      brfReturnFlightDate: flight.brfReturnFlightDate,
      brfDepartureAirport: flight.brfDepartureAirport,
      brfArrivalAirport: flight.brfArrivalAirport,
      cnxModCosts: flight.cnxModCosts || [],
    });
  }, [flight]);

  useEffect(() => {
    const flightIdentifier = flight?.id ?? flight?.bookingReference;
    if (typeof onTotalChange === 'function' && flightIdentifier) {
      onTotalChange(flightIdentifier, numericTotal, currentSelectedFlight.currency);
    }
  }, [
    flight?.id,
    flight?.bookingReference,
    currentSelectedFlight.xbagCost,
    currentSelectedFlight.flightCost,
    currentSelectedFlight.otherTransportCost,
    currentSelectedFlight.hotelCost,
    currentSelectedFlight.cnxModCosts,
    currentSelectedFlight.currency,
    numericTotal,
    onTotalChange,
  ]);

  const handleAddFlightToTicket = async () => {
    const errors = {};
    const excludedFields = [
      'id',
      'via',
      'xbagCost',
      'flightCost',
      'hotelCost',
      'flightArrivalTime',
      'brfReturnFlightDate',
      'brfDepartureAirport',
      'brfArrivalAirport',
      'otherTransportCost',
      'hotelCheckOutDate',
      'cnxModCosts', // Exclude from generic validation - handled separately
    ];

    Object.keys(currentSelectedFlight).forEach((field) => {
      if (excludedFields.includes(field)) return;
      if (!currentSelectedFlight[field]) {
        errors[field] = `Please fill in the ${field}`;
      }
    });

    if (!flyToWorkTicketInfo.isHotelNeeded) {
      delete errors.hotelName;
      delete errors.hotelCheckInDate;
      delete errors.hotelCost;
    }

    if (!flyToWorkTicketInfo.isBookReturnFlight) {
      delete errors.brfReturnFlightDate;
      delete errors.brfDepartureAirport;
      delete errors.brfArrivalAirport;
    }

    // Validate CNX/MOD cost fields when checkbox is checked (cnxModCosts array has entries)
    const cnxModCosts = currentSelectedFlight.cnxModCosts || [];
    if (cnxModCosts.length > 0) {
      const cnxModCost = cnxModCosts[0];

      // Validate cost field - must be a valid number (not empty)
      if (cnxModCost.cost === '' || cnxModCost.cost === null || cnxModCost.cost === undefined) {
        errors.cnxModCost = 'Cost is required when CNX/MOD Cost is checked';
      } else if (Number.isNaN(Number(cnxModCost.cost))) {
        errors.cnxModCost = 'Cost must be a valid number';
      }

      // Validate who field - must be selected (not empty)
      if (!cnxModCost.who || cnxModCost.who.trim() === '') {
        errors.cnxModWho = 'Who is required when CNX/MOD Cost is checked';
      }

      // Validate reason field - must be selected (not empty)
      if (!cnxModCost.reason || cnxModCost.reason.trim() === '') {
        errors.cnxModReason = 'Reason is required when CNX/MOD Cost is checked';
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationFlightFormErrors(errors);
      return;
    }

    // Renew the editing lock right before saving so a lock that expired while
    // the user was filling in the form (e.g. CNX/MOD costs) does not cause the
    // backend to reject the update with a 409. If the lock can no longer be
    // renewed, resetLockTimers surfaces the blocked popup and we abort here.
    if (isLockRequired && typeof resetLockTimers === 'function' && !(await resetLockTimers())) {
      return;
    }

    const payload = {
      flyToWorkTicketId: flyToWorkTicketInfo.id,
      ...currentSelectedFlight,
    };

    dispatch(setLoader(true));
    setShowAddFlightModal(false);
    FlightTicketApiService.updateFlightInfo(flyToWorkTicketInfo.id, payload)
      .then((response) => {
        if (response?.ok) {
          toastr.success('Success', response.message);

          // Update the total immediately after successful API call
          const flightIdentifier = currentSelectedFlight?.id ?? currentSelectedFlight?.bookingReference;
          if (typeof onTotalChange === 'function' && flightIdentifier) {
            onTotalChange(flightIdentifier, numericTotal, currentSelectedFlight.currency);
          }
        } else {
          toastr.error('Error', response?.message || 'Failed to save flight data. Please try again.');
        }
      })
      .catch((error) => {
        // Handle API errors and display user-friendly messages
        console.error('Error saving flight:', error);

        const status = error?.response?.status;
        const backendCode = error?.response?.data?.code;
        const backendMessage = error?.response?.data?.error;

        // A 409 means the edit was rejected because of the ticket lock
        // (another user holds it, or this user's lock expired).
        if (status === 409) {
          if (backendCode === 'TICKET_LOCKED') {
            toastr.error(
              'Ticket locked',
              backendMessage || 'This ticket is currently being edited by another user. Please try again later.'
            );
          } else {
            // TICKET_LOCK_REQUIRED or any other 409
            toastr.error(
              'Editing session expired',
              'Your editing session for this ticket has expired. Please reopen the ticket and try again.'
            );
          }
        } else {
          toastr.error('Error', backendMessage || 'Failed to save flight data. Please try again.');
        }
      })
      .finally(() => {
        dispatch(setLoader(false));
        // Add a small delay to ensure the total update is processed before refresh
        setTimeout(() => {
          setRefreshComponent((prev) => prev + 1);
        }, 100);
      });
  };

  const handleRemoveFlightFromTicket = async () => {
    // Renew the editing lock before deleting so an expired lock does not cause
    // the backend to reject the delete with a 409.
    if (isLockRequired && typeof resetLockTimers === 'function' && !(await resetLockTimers())) {
      return;
    }

    dispatch(setLoader(true));
    FlightTicketApiService.deleteFlightInfo(flyToWorkTicketInfo.id, currentSelectedFlight.bookingReference)
      .then((response) => {
        if (response.ok) {
          toastr.success('Success', response.message);
        } else {
          toastr.error('Error', response.message);
        }
      })
      .finally(() => {
        dispatch(setLoader(false));
        setRefreshComponent((prev) => prev + 1);
      });
  };

  const isDisabled = !userRoles.includes(DS_F2W_Business_Travel_Team);
  const parseBooleanOrNull = (value) => value === 'true' || value === true;

  return (
    <Card className="staff-profile-card mb-3">
      <Card.Header hidden={!currentSelectedFlight.id}>
        <i className="fa fa-plane me-2"></i>Flight ID: {currentSelectedFlight.id}
      </Card.Header>
      <Card.Body>
        <FlightDetailsSection
          currentSelectedFlight={currentSelectedFlight}
          setCurrentSelectedFlight={setCurrentSelectedFlight}
          validationFlightFormErrors={validationFlightFormErrors}
          setValidationFlightFormErrors={setValidationFlightFormErrors}
          mappedAirports={options.airports}
          isDisabled={isDisabled}
        />
        <TravelTypeSection
          currentSelectedFlight={currentSelectedFlight}
          setCurrentSelectedFlight={setCurrentSelectedFlight}
          validationFlightFormErrors={validationFlightFormErrors}
          setValidationFlightFormErrors={setValidationFlightFormErrors}
          mappedTravelTypes={options.mappedTravelTypes}
          mappedPaymentMethods={options.mappedPaymentMethods}
          isDisabled={isDisabled}
        />
        <FlightCostSection
          currentSelectedFlight={currentSelectedFlight}
          setCurrentSelectedFlight={setCurrentSelectedFlight}
          validationFlightFormErrors={validationFlightFormErrors}
          setValidationFlightFormErrors={setValidationFlightFormErrors}
          validLuggageSelectOptions={options.validLuggageSelectOptions}
          costCentreOptions={options.costCentreOptions}
          currencySelectOptions={options.currencySelectOptions}
          totalCost={totalCost}
          isDisabled={isDisabled}
          isHotelDisabled={!flyToWorkTicketInfo.isHotelNeeded}
        />
        <HotelSection
          currentSelectedFlight={currentSelectedFlight}
          setCurrentSelectedFlight={setCurrentSelectedFlight}
          validationFlightFormErrors={validationFlightFormErrors}
          setValidationFlightFormErrors={setValidationFlightFormErrors}
          isHotelNeeded={parseBooleanOrNull(flyToWorkTicketInfo.isHotelNeeded)}
          ticketStatus={flyToWorkTicketInfo.status}
        />
        <CNXModCostSection
          currentSelectedFlight={currentSelectedFlight}
          setCurrentSelectedFlight={setCurrentSelectedFlight}
          validationFlightFormErrors={validationFlightFormErrors}
          setValidationFlightFormErrors={setValidationFlightFormErrors}
          whoOptions={options.whoOptions}
          reasonOptions={options.reasonOptions}
          isDisabled={isDisabled}
        />
      </Card.Body>
      <Card.Footer>
        <div className="d-flex gap-2">
          {userRoles.includes(DS_F2W_Business_Travel_Team) && (
            <Button onClick={handleAddFlightToTicket} variant="primary" size="sm">
              <i className="fa fa-save me-1"></i>
              Update Flight
            </Button>
          )}
          {userRoles.includes(DS_F2W_Business_Travel_Team) && !showCloseButtonOnModal && (
            <Button onClick={handleRemoveFlightFromTicket} variant="danger" size="sm">
              <i className="fa fa-trash me-1"></i>
              Remove
            </Button>
          )}
          {userRoles.includes(DS_F2W_Business_Travel_Team) && showCloseButtonOnModal && (
            <Button onClick={() => setShowAddFlightModal(false)} variant="secondary" size="sm">
              <i className="fa fa-times me-1"></i>
              Close
            </Button>
          )}
        </div>
      </Card.Footer>
    </Card>
  );
};

FlightCard.propTypes = {
  flyToWorkTicketInfo: FlightTicketPropType.isRequired,
  setRefreshComponent: PropTypes.func.isRequired,
  flight: PropTypes.object.isRequired,
  setShowAddFlightModal: PropTypes.func.isRequired,
  showCloseButtonOnModal: PropTypes.bool,
  onTotalChange: PropTypes.func,
  isLockRequired: PropTypes.bool,
  resetLockTimers: PropTypes.func,
};

export default FlightCard;
